import React, { useState, useEffect } from 'react';
import { api } from '../../api/client';
import { RefreshCw, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import MetricCard from '../../components/MetricCard';

const AmazonSettlements = () => {
  const [activeTab, setActiveTab] = useState('transactions');
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 25, total: 0 });
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    fulfillmentType: 'all',
    status: 'all',
    search: ''
  });
  const [analytics, setAnalytics] = useState(null);
  const [feeBreakdown, setFeeBreakdown] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [selectedEnvironment, setSelectedEnvironment] = useState('sandbox');

  const getTenantId = () => localStorage.getItem('tenant_id') || 'tenant-123';

  // Sync Now handler
  const handleSyncNow = async () => {
    setSyncing(true);
    try {
      const tenantId = getTenantId();
      await api.post('/api/v1/amazon-settlements/sync', { 
        tenant_id: tenantId,
        environment: selectedEnvironment
      });
      loadTransactions();
      if (activeTab === 'analytics') loadAnalytics();
      if (activeTab === 'fees') loadFeeBreakdown();
    } catch (error) {
      console.error('Sync error:', error);
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    loadTransactions();
  }, [pagination.page, filters]);

  useEffect(() => {
    if (activeTab === 'analytics') loadAnalytics();
    else if (activeTab === 'fees') loadFeeBreakdown();
  }, [activeTab]);

  const loadTransactions = async () => {
    try {
      setLoading(true);
      const tenantId = getTenantId();
      const params = new URLSearchParams({
        tenant_id: tenantId,
        page: pagination.page.toString(),
        page_size: pagination.pageSize.toString(),
        ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v && v !== 'all'))
      });

      const data = await api.get(`/api/v1/amazon-settlements/transactions?${params}`);
      setTransactions(data.transactions || []);
      setPagination(prev => ({ ...prev, total: data.total || 0 }));
    } catch (err) {
      console.error('Error loading transactions:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadAnalytics = async () => {
    try {
      const tenantId = getTenantId();
      const data = await api.get(`/api/v1/amazon-settlements/analytics?tenant_id=${tenantId}`);
      setAnalytics(data);
    } catch (err) {
      console.error('Error loading analytics:', err);
    }
  };

  const loadFeeBreakdown = async () => {
    try {
      const tenantId = getTenantId();
      const data = await api.get(`/api/v1/amazon-settlements/fee-breakdown?tenant_id=${tenantId}`);
      setFeeBreakdown(data);
    } catch (err) {
      console.error('Error loading fee breakdown:', err);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const totalPages = Math.ceil(pagination.total / pagination.pageSize);

  // Calculate summary metrics from transactions
  const summaryMetrics = {
    totalSettled: transactions.reduce((sum, t) => sum + (t.amount || 0), 0),
    pendingAmount: transactions.filter(t => t.status === 'pending').reduce((sum, t) => sum + (t.amount || 0), 0),
    fbaCount: transactions.filter(t => t.fulfillment_type === 'FBA').length,
    fbmCount: transactions.filter(t => t.fulfillment_type === 'FBM').length
  };

  return (
    <div className="space-y-3">
      {/* Compact Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-bold text-white">📋 Amazon Settlements</h2>
          <p className="text-[10px] text-gray-400">
            {pagination.total} transactions • {selectedEnvironment === 'production' ? '🚀 Production' : '🧪 Sandbox'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={selectedEnvironment}
            onChange={(e) => setSelectedEnvironment(e.target.value)}
            className="bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-xs text-white"
          >
            <option value="sandbox">🧪 Sandbox</option>
            <option value="production">🚀 Production</option>
          </select>
          <button 
            onClick={handleSyncNow} 
            disabled={syncing}
            className="px-3 py-1.5 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1.5"
          >
            <RefreshCw className={`w-3 h-3 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Syncing...' : 'Sync'}
          </button>
        </div>
      </div>

      {/* Summary Metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <MetricCard
          icon="💰"
          label="TOTAL SETTLED"
          value={formatCurrency(summaryMetrics.totalSettled)}
          subtitle={`${pagination.total} transactions`}
          status={summaryMetrics.totalSettled > 0 ? 'green' : 'default'}
        />
        <MetricCard
          icon="⏳"
          label="PENDING"
          value={formatCurrency(summaryMetrics.pendingAmount)}
          subtitle="awaiting settlement"
          status={summaryMetrics.pendingAmount > 0 ? 'yellow' : 'green'}
        />
        <MetricCard
          icon="📦"
          label="FBA"
          value={summaryMetrics.fbaCount.toString()}
          subtitle="transactions"
          status="default"
        />
        <MetricCard
          icon="🏪"
          label="FBM"
          value={summaryMetrics.fbmCount.toString()}
          subtitle="transactions"
          status="default"
        />
      </div>

      {/* Compact Tabs */}
      <div className="flex gap-1 border-b border-slate-700">
        {[
          { id: 'transactions', label: 'Transactions', icon: '📋' },
          { id: 'fees', label: 'Fee Breakdown', icon: '💵' },
          { id: 'analytics', label: 'Analytics', icon: '📊' }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-3 py-1.5 text-xs font-medium border-b-2 transition-colors ${
              activeTab === tab.id
                ? 'border-blue-500 text-blue-400'
                : 'border-transparent text-gray-400 hover:text-gray-300'
            }`}
          >
            <span className="mr-1">{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Transactions Tab */}
      {activeTab === 'transactions' && (
        <div className="space-y-3">
          {/* Compact Filters */}
          <div className="bg-slate-800 rounded-lg p-3">
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-2">
              <input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
                className="bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-xs text-white"
                placeholder="From"
              />
              <input
                type="date"
                value={filters.dateTo}
                onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
                className="bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-xs text-white"
                placeholder="To"
              />
              <select
                value={filters.fulfillmentType}
                onChange={(e) => setFilters({ ...filters, fulfillmentType: e.target.value })}
                className="bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-xs text-white"
              >
                <option value="all">All Types</option>
                <option value="FBA">FBA</option>
                <option value="FBM">FBM</option>
              </select>
              <select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                className="bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-xs text-white"
              >
                <option value="all">All Status</option>
                <option value="completed">Completed</option>
                <option value="pending">Pending</option>
              </select>
              <div className="relative">
                <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-3 h-3 text-gray-500" />
                <input
                  type="text"
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  className="w-full bg-slate-900 border border-slate-700 rounded pl-7 pr-2 py-1.5 text-xs text-white placeholder-gray-500"
                  placeholder="Order ID or SKU..."
                />
              </div>
            </div>
          </div>

          {/* Compact Table */}
          <div className="bg-slate-800 rounded-lg overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="w-5 h-5 animate-spin text-blue-400" />
                <span className="ml-2 text-xs text-gray-400">Loading transactions...</span>
              </div>
            ) : transactions.length === 0 ? (
              <div className="text-center py-8">
                <div className="text-4xl mb-3">📊</div>
                <h3 className="text-sm font-semibold text-white mb-2">No Transactions Found</h3>
                <p className="text-xs text-gray-400 mb-4">
                  Upload settlement reports or sync with Amazon SP-API
                </p>
                <button 
                  onClick={handleSyncNow}
                  className="px-4 py-2 bg-blue-600 text-white rounded text-xs hover:bg-blue-700"
                >
                  Sync Now
                </button>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-900">
                      <tr>
                        <th className="px-3 py-2 text-left text-[10px] uppercase text-gray-400 font-semibold">Date</th>
                        <th className="px-3 py-2 text-left text-[10px] uppercase text-gray-400 font-semibold">Order ID</th>
                        <th className="px-3 py-2 text-left text-[10px] uppercase text-gray-400 font-semibold">SKU</th>
                        <th className="px-3 py-2 text-center text-[10px] uppercase text-gray-400 font-semibold">Type</th>
                        <th className="px-3 py-2 text-right text-[10px] uppercase text-gray-400 font-semibold">Amount</th>
                        <th className="px-3 py-2 text-center text-[10px] uppercase text-gray-400 font-semibold">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-700">
                      {transactions.map((txn, idx) => (
                        <tr key={idx} className="hover:bg-slate-700/50 transition-colors">
                          <td className="px-3 py-2 text-xs text-gray-300">
                            {formatDate(txn.posted_date || txn.date)}
                          </td>
                          <td className="px-3 py-2 text-xs font-mono text-white">
                            {txn.order_id || txn.amazon_order_id || 'N/A'}
                          </td>
                          <td className="px-3 py-2 text-xs font-mono text-gray-300">
                            {txn.sku || 'N/A'}
                          </td>
                          <td className="px-3 py-2 text-center">
                            <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${
                              txn.fulfillment_type === 'FBA' 
                                ? 'bg-blue-500/20 text-blue-400' 
                                : 'bg-gray-500/20 text-gray-400'
                            }`}>
                              {txn.fulfillment_type || txn.fulfillment || 'N/A'}
                            </span>
                          </td>
                          <td className={`px-3 py-2 text-xs text-right font-medium ${
                            (txn.amount || 0) >= 0 ? 'text-green-400' : 'text-red-400'
                          }`}>
                            {formatCurrency(txn.amount || txn.total)}
                          </td>
                          <td className="px-3 py-2 text-center">
                            <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${
                              txn.status === 'completed' 
                                ? 'bg-green-500/20 text-green-400'
                                : txn.status === 'pending'
                                  ? 'bg-yellow-500/20 text-yellow-400'
                                  : 'bg-gray-500/20 text-gray-400'
                            }`}>
                              {txn.status || 'Completed'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="px-3 py-2 border-t border-slate-700 flex items-center justify-between">
                  <span className="text-xs text-gray-400">
                    Page {pagination.page} of {totalPages || 1}
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                      disabled={pagination.page === 1}
                      className="px-2 py-1 text-xs bg-slate-700 text-white rounded disabled:opacity-50 hover:bg-slate-600 flex items-center"
                    >
                      <ChevronLeft className="w-3 h-3" /> Prev
                    </button>
                    <button
                      onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                      disabled={pagination.page >= totalPages}
                      className="px-2 py-1 text-xs bg-slate-700 text-white rounded disabled:opacity-50 hover:bg-slate-600 flex items-center"
                    >
                      Next <ChevronRight className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Fee Breakdown Tab */}
      {activeTab === 'fees' && (
        <div className="space-y-3">
          {/* Fee Summary Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <MetricCard
              icon="💰"
              label="TOTAL FEES"
              value={formatCurrency(feeBreakdown?.total_fees || 0)}
              subtitle="all fees"
              status="red"
            />
            <MetricCard
              icon="📦"
              label="FBA FEES"
              value={formatCurrency(feeBreakdown?.fba_fees || 0)}
              subtitle="fulfillment"
              status="default"
            />
            <MetricCard
              icon="🏪"
              label="FBM FEES"
              value={formatCurrency(feeBreakdown?.fbm_fees || 0)}
              subtitle="merchant"
              status="default"
            />
            <MetricCard
              icon="↩️"
              label="REFUNDS"
              value={formatCurrency(feeBreakdown?.refunds || 0)}
              subtitle="returned"
              status={feeBreakdown?.refunds > 0 ? 'yellow' : 'green'}
            />
          </div>

          {/* Fee Types Table */}
          <div className="bg-slate-800 rounded-lg overflow-hidden">
            <div className="px-3 py-2 border-b border-slate-700">
              <h3 className="text-xs font-semibold text-white">Fee Types</h3>
            </div>
            {feeBreakdown?.fee_types?.length > 0 ? (
              <table className="w-full">
                <thead className="bg-slate-900">
                  <tr>
                    <th className="px-3 py-2 text-left text-[10px] uppercase text-gray-400">Fee Type</th>
                    <th className="px-3 py-2 text-right text-[10px] uppercase text-gray-400">Amount</th>
                    <th className="px-3 py-2 text-right text-[10px] uppercase text-gray-400">%</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700">
                  {feeBreakdown.fee_types.map((fee, idx) => (
                    <tr key={idx} className="hover:bg-slate-700/50">
                      <td className="px-3 py-2 text-xs text-white">{fee.type}</td>
                      <td className="px-3 py-2 text-xs text-right text-gray-300 font-medium">{formatCurrency(fee.amount)}</td>
                      <td className="px-3 py-2 text-xs text-right text-gray-400">{fee.percentage?.toFixed(1)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="text-center py-8">
                <div className="text-3xl mb-2">💵</div>
                <p className="text-sm text-gray-400">No fee data available</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Analytics Tab */}
      {activeTab === 'analytics' && (
        <div className="space-y-3">
          {/* Analytics Summary Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <MetricCard
              icon="💵"
              label="TOTAL SETTLEMENT"
              value={formatCurrency(analytics?.total_settlement || 0)}
              subtitle="gross amount"
              status="green"
            />
            <MetricCard
              icon="📊"
              label="AVG ORDER"
              value={formatCurrency(analytics?.avg_order_value || 0)}
              subtitle="per transaction"
              status="default"
            />
            <MetricCard
              icon="📦"
              label="FBA %"
              value={`${analytics?.fba_percentage?.toFixed(0) || 0}%`}
              subtitle="of orders"
              status="default"
            />
            <MetricCard
              icon="🛒"
              label="TOTAL ORDERS"
              value={(analytics?.total_orders || 0).toString()}
              subtitle="processed"
              status="default"
            />
          </div>

          {/* Top SKUs */}
          <div className="bg-slate-800 rounded-lg overflow-hidden">
            <div className="px-3 py-2 border-b border-slate-700">
              <h3 className="text-xs font-semibold text-white">🏆 Top 5 SKUs by Revenue</h3>
            </div>
            {analytics?.top_skus?.length > 0 ? (
              <table className="w-full">
                <thead className="bg-slate-900">
                  <tr>
                    <th className="px-3 py-2 text-left text-[10px] uppercase text-gray-400">#</th>
                    <th className="px-3 py-2 text-left text-[10px] uppercase text-gray-400">SKU</th>
                    <th className="px-3 py-2 text-left text-[10px] uppercase text-gray-400">Product</th>
                    <th className="px-3 py-2 text-right text-[10px] uppercase text-gray-400">Revenue</th>
                    <th className="px-3 py-2 text-right text-[10px] uppercase text-gray-400">Orders</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700">
                  {analytics.top_skus.slice(0, 5).map((sku, idx) => (
                    <tr key={idx} className="hover:bg-slate-700/50">
                      <td className="px-3 py-2 text-xs text-gray-400 font-medium">{idx + 1}</td>
                      <td className="px-3 py-2 text-xs font-mono text-white">{sku.sku}</td>
                      <td className="px-3 py-2 text-xs text-gray-300 truncate max-w-[150px]">{sku.product_name || 'N/A'}</td>
                      <td className="px-3 py-2 text-xs text-right text-green-400 font-medium">{formatCurrency(sku.revenue)}</td>
                      <td className="px-3 py-2 text-xs text-right text-gray-400">{sku.orders}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="text-center py-8">
                <div className="text-3xl mb-2">📊</div>
                <p className="text-sm text-gray-400">No analytics data available</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AmazonSettlements;
